/**
 * Service Worker registration and management utilities
 */

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

/**
 * Register the service worker
 */
export function registerServiceWorker(config?: Config) {
  // Allow service worker in both development and production
  if ("serviceWorker" in navigator) {
    const publicUrl = new URL(import.meta.env.BASE_URL || "", window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "Service Worker: Running in localhost. Service worker registered."
          );
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log("Service Worker: Registered successfully", registration);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              console.log("Service Worker: New content available, activating immediately...");
              // Tell the new service worker to skip waiting and take control
              installingWorker.postMessage({ type: 'SKIP_WAITING' });

              // Listen for the controllerchange event to reload the page
              navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log("Service Worker: New service worker activated, reloading...");
                window.location.reload();
              });

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log("Service Worker: Content cached for offline use.");
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error("Service Worker: Registration failed:", error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log("Service Worker: No internet connection. Running in offline mode.");
    });
}

/**
 * Unregister the service worker
 */
export function unregisterServiceWorker() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log("Service Worker: Unregistered");
      })
      .catch((error) => {
        console.error("Service Worker: Error unregistering:", error);
      });
  }
}

/**
 * Get the active service worker registration
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if ("serviceWorker" in navigator) {
    try {
      return await navigator.serviceWorker.ready;
    } catch (error) {
      console.error("Service Worker: Error getting registration:", error);
      return null;
    }
  }
  return null;
}

/**
 * Send a message to the service worker
 */
export function sendMessageToServiceWorker(message: unknown) {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
}

/**
 * Show a notification using the service worker
 */
export async function showServiceWorkerNotification(
  title: string,
  options?: NotificationOptions
): Promise<void> {
  const registration = await getServiceWorkerRegistration();
  if (registration) {
    await registration.showNotification(title, {
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      ...options,
    });
  } else {
    console.warn("Service Worker: No registration found for notification");
  }
}

/**
 * Subscribe to push notifications (requires backend setup)
 */
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    console.warn("Service Worker: No registration found for push subscription");
    return null;
  }

  try {
    // You'll need to replace this with your actual VAPID public key
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

    if (!vapidPublicKey) {
      console.warn("Service Worker: VAPID public key not configured");
      return null;
    }

    const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedVapidKey,
    });

    console.log("Service Worker: Push subscription successful", subscription);
    return subscription;
  } catch (error) {
    console.error("Service Worker: Error subscribing to push:", error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return false;
  }

  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const successful = await subscription.unsubscribe();
      console.log("Service Worker: Push unsubscription successful");
      return successful;
    }
    return true;
  } catch (error) {
    console.error("Service Worker: Error unsubscribing from push:", error);
    return false;
  }
}

/**
 * Get the current push subscription
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return null;
  }

  try {
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error("Service Worker: Error getting push subscription:", error);
    return null;
  }
}

/**
 * Helper function to convert VAPID key
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
